import { Type } from "@sinclair/typebox";
import { JsonValueSchema } from "./shared.js";
const text = Type.String();
const index = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const status = Type.Union([
    Type.Literal("in_progress"),
    Type.Literal("completed"),
    Type.Literal("incomplete"),
]);
const nullableText = Type.Union([text, Type.Null()]);
/** 原生被动输出的闭合合同；执行调用及资源引用没有登记入口。 */
export const NativeModelContentSchema = Type.Union([
    Type.Object({
        type: Type.Literal("output_text"),
        text,
        annotations: Type.Optional(Type.Array(JsonValueSchema)),
        logprobs: Type.Optional(Type.Array(JsonValueSchema)),
    }, { additionalProperties: false }),
    Type.Object({ type: Type.Literal("refusal"), refusal: text }, { additionalProperties: false }),
    Type.Object({ type: Type.Literal("summary_text"), text }, { additionalProperties: false }),
    Type.Object({ type: Type.Literal("reasoning_text"), text }, { additionalProperties: false }),
]);
export const NativeModelItemSchema = Type.Union([
    Type.Object({
        type: Type.Literal("message"),
        id: Type.Optional(text),
        role: Type.Literal("assistant"),
        status: Type.Optional(status),
        content: Type.Array(NativeModelContentSchema),
        phase: Type.Optional(Type.Union([Type.Literal("commentary"), Type.Literal("final_answer"), Type.Null()])),
    }, { additionalProperties: false }),
    Type.Object({
        type: Type.Literal("reasoning"),
        id: Type.Optional(nullableText),
        summary: Type.Array(Type.Object({ type: Type.Literal("summary_text"), text }, { additionalProperties: false })),
        content: Type.Optional(Type.Union([
            Type.Array(Type.Object({ type: Type.Literal("reasoning_text"), text }, { additionalProperties: false })),
            Type.Null(),
        ])),
        encrypted_content: Type.Optional(nullableText),
        status: Type.Optional(status),
    }, { additionalProperties: false }),
    Type.Object({
        type: Type.Literal("compaction"),
        id: Type.Optional(nullableText),
        encrypted_content: Type.String({ minLength: 1 }),
    }, { additionalProperties: false }),
]);
const usage = Type.Object({
    input_tokens: index,
    output_tokens: index,
    total_tokens: Type.Optional(index),
    input_tokens_details: Type.Optional(JsonValueSchema),
    output_tokens_details: Type.Optional(JsonValueSchema),
}, { additionalProperties: false });
// response 的元数据由上游管理；SDK 可执行的 output 始终使用闭合合同。
const response = Type.Object({
    output: Type.Array(NativeModelItemSchema),
    usage: Type.Optional(Type.Union([usage, Type.Null()])),
}, { additionalProperties: true });
const event = (name, fields) => Type.Object({ type: Type.Literal(name), sequence_number: Type.Optional(index), ...fields }, { additionalProperties: false });
const location = { item_id: text, output_index: index };
/** SSE 事件由模型协议登记，未知事件失败关闭，不沿用 SDK 的宽松解析。 */
export const NativeModelEventSchema = Type.Union([
    ...[
        "response.created",
        "response.in_progress",
        "response.queued",
        "response.completed",
        "response.failed",
        "response.incomplete",
    ].map((name) => event(name, { response })),
    ...["response.output_item.added", "response.output_item.done"].map((name) => event(name, { output_index: index, item: NativeModelItemSchema })),
    ...["response.content_part.added", "response.content_part.done"].map((name) => event(name, { ...location, content_index: index, part: NativeModelContentSchema })),
    ...["response.reasoning_summary_part.added", "response.reasoning_summary_part.done"].map((name) => event(name, {
        ...location,
        summary_index: index,
        part: Type.Object({ type: Type.Literal("summary_text"), text }, { additionalProperties: false }),
    })),
    event("response.output_text.delta", {
        ...location,
        content_index: index,
        delta: text,
        logprobs: Type.Optional(Type.Array(JsonValueSchema)),
        obfuscation: Type.Optional(text),
    }),
    event("response.output_text.done", {
        ...location,
        content_index: index,
        text,
        logprobs: Type.Optional(Type.Array(JsonValueSchema)),
    }),
    ...["response.reasoning_summary_text.delta", "response.reasoning_summary_text.done"].map((name) => event(name, {
        ...location,
        summary_index: index,
        [name.endsWith("delta") ? "delta" : "text"]: text,
        obfuscation: Type.Optional(text),
    })),
    ...["response.reasoning_text.delta", "response.reasoning_text.done"].map((name) => event(name, {
        ...location,
        content_index: index,
        [name.endsWith("delta") ? "delta" : "text"]: text,
        obfuscation: Type.Optional(text),
    })),
    ...["response.refusal.delta", "response.refusal.done"].map((name) => event(name, {
        ...location,
        content_index: index,
        [name.endsWith("delta") ? "delta" : "refusal"]: text,
    })),
    event("error", {
        code: Type.Optional(nullableText),
        message: text,
        param: Type.Optional(nullableText),
    }),
]);
//# sourceMappingURL=model-events.js.map